// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showPrint = "showPrint",
    showContinueShopping = "showContinueShopping",
    showAddToList = "showAddToList",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showPrint]: boolean;
        [fields.showContinueShopping]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCartState(state, state.pages.orderConfirmation.cartId),
    homePageUrl: getHomePageUrl(state),
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderConfirmationActionsStyles {
    printButton?: ButtonPresentationProps;
    printLink?: LinkPresentationProps;
    continueButton?: ButtonPresentationProps;
    continueLink?: LinkPresentationProps;
    addToListButton?: ButtonPresentationProps;
    addToListLink?: LinkPresentationProps;
}

export const orderConfirmationActionsStyles: OrderConfirmationActionsStyles = {
    addToListButton: { variant: "tertiary" },
};

const styles = orderConfirmationActionsStyles;

const OrderConfirmationActions: FC<Props> = props => {
    const history = useHistory();
    const toasterContext = useToaster();

    const continueClickHandler = () => {
        history.push(props.homePageUrl);
    };

    const addToListHandler = () => {
        const productInfos = props?.cartState?.value?.cartLines?.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        })) as Omit<ProductInfo, "productDetailPath">[];

        if (!props.wishListSettings.allowMultipleWishLists) {
            props.addToWishList({
                productInfos,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        props.setAddToListModalIsOpen({ modalIsOpen: true, productInfos });
    };

    return (
        <OrderActionSet
            fields={props.fields}
            actions={[
                {
                    preset: "print",
                    linkStyles: styles.printLink,
                    buttonStyles: styles.printButton,
                    field: fields.showPrint,
                },
                {
                    key: "continue",
                    label: translate("Continue Shopping"),
                    onClick: continueClickHandler,
                    buttonStyles: styles.continueButton,
                    linkStyles: styles.continueLink,
                    isSticky: true,
                    testSelector: "continueBtn",
                    field: fields.showContinueShopping,
                },
                {
                    key: "addToList",
                    label: translate("Add to List"),
                    onClick: addToListHandler,
                    buttonStyles: styles.addToListButton,
                    linkStyles: styles.addToListLink,
                    isSticky: true,
                    testSelector: "addToListBtn",
                    field: fields.showAddToList,
                    isVisible: !!(props.wishListSettings && props?.cartState?.value?.cartLines),
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationActions),
    definition: {
        group: "Order Confirmation",
        allowedContexts: [],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showPrint },
            { name: fields.showContinueShopping },
            { name: fields.showAddToList },
        ]),
    },
};

export default widgetModule;
